/**
 * @class Tooltip
 * @description A lightweight and responsive tooltip.
 */
import React from "react";

import Portal, { isBrowser } from "./Portal";
import Styles from "./Tooltip.module.css";
import positions from "./position";

const resizeThrottle = 100;
const resizeThreshold = 5;

const stopProp = (e) => e.stopPropagation();

/**
 * @class Tooltip
 * @description A lightweight and responsive tooltip.
 * @param {Object} props
 * @param {Boolean} [props.arrow=true] - Whether to show the arrow.
 * @param {Number} [props.arrowSize=5] - The size of the arrow.
 * @param {String} [props.background=""] - The background color of the tooltip.
 * @param {String} [props.className=""] - Additional classes to apply to the tooltip.
 * @param {ReactNode} props.children - The content that will trigger the tooltip.
 * @param {String} [props.color=""] - The text color of the tooltip.
 * @param {ReactNode} [props.content] - The content of the tooltip.
 * @param {String} [props.contentClassName=""] - Additional classes to apply to the tooltip content.
 * @param {String} [props.arrowClassName=""] - Additional classes to apply to the tooltip arrow.
 * @param {String} [props.direction="up"] - The direction the tooltip should point.
 * @param {Number} [props.distance] - The distance the tooltip should be from the target.
 * @param {String} [props.eventOff] - The event that will hide the tooltip.
 * @param {String} [props.eventOn] - The event that will show the tooltip.
 * @param {String} [props.eventToggle] - The event that will toggle the tooltip.
 * @param {Boolean} [props.forceDirection=false] - Whether to force the tooltip to point in the specified direction.
 * @param {Number} [props.hoverdelay=200] - The delay before the tooltip will hide on hover.
 * @param {Boolean} [props.isOpen] - Whether the tooltip is open.
 * @param {Number} [props.mouseOutDelay] - The delay before the tooltip will hide on mouse out.
 * @param {String} [props.padding="10px"] - The padding of the tooltip.
 * @param {Object} [props.styles={}] - Additional styles to apply to the tooltip.
 * @param {String} [props.tagName="div"] - The tag name of the tooltip.
 * @param {String} [props.tipContentClassName] - Additional classes to apply to the tooltip content.
 * @param {Boolean} [props.tipContentHover=false] - Whether to show the tooltip on hover of the content.
 * @param {Boolean} [props.useHover=true] - Whether to show the tooltip on hover of the target.
 * @param {Function} [props.onToggle] - A function that will be called when the tooltip is toggled.
 * @param {ReactNode} [props.arrowContent] - The content of the arrow.
 * @returns {ReactNode} The tooltip component.
 * @example
 * <Tooltip content="Hello World">
 *  <button>Hover me</button>
 * </Tooltip>
 */
class Tooltip extends React.Component {
	static defaultProps = {
		arrow: true,
		arrowSize: 5,
		background: "",
		className: "",
		color: "",
		direction: "up",
		distance: undefined,
		eventOff: undefined,
		eventOn: undefined,
		eventToggle: undefined,
		forceDirection: false,
		hoverdelay: 200,
		isOpen: undefined,
		mouseOutDelay: undefined,
		padding: "10px",
		contentClassName: "",
		arrowClassName: "",
		styles: {},
		tagName: "div",
		tipContentHover: false,
		tipContentClassName: undefined,
		useHover: true,
		onToggle: undefined,
		arrowContent: null,
	};

	static getDerivedStateFromProps(nextProps) {
		return isBrowser && nextProps.isOpen ? { hasBeenShown: true } : null;
	}

	debounceTimeout = false;

	hoverTimeout = false;

	constructor() {
		super();

		this.state = {
			showTip: false,
			hasHover: false,
			ignoreShow: false,
			hasBeenShown: false,
		};

		this.showTip = this.showTip.bind(this);
		this.hideTip = this.hideTip.bind(this);
		this.checkHover = this.checkHover.bind(this);
		this.toggleTip = this.toggleTip.bind(this);
		this.startHover = this.startHover.bind(this);
		this.endHover = this.endHover.bind(this);
		this.listenResizeScroll = this.listenResizeScroll.bind(this);
		this.handleResizeScroll = this.handleResizeScroll.bind(this);
		this.bodyTouchStart = this.bodyTouchStart.bind(this);
		this.bodyTouchEnd = this.bodyTouchEnd.bind(this);
		this.targetTouchStart = this.targetTouchStart.bind(this);
		this.targetTouchEnd = this.targetTouchEnd.bind(this);
	}

	componentDidMount() {
		// if the isOpen prop is passed on first render we need to immediately trigger a second render,
		// because the tip ref is needed to calculate the position
		if (this.props.isOpen) {
			// eslint-disable-next-line react/no-did-mount-set-state
			this.setState({ isOpen: true });
		}

		this.scrollParent = document.querySelector("#main-container");

		window.addEventListener("resize", this.listenResizeScroll);
		this.scrollParent.addEventListener("scroll", this.listenResizeScroll);
		window.addEventListener("touchstart", this.bodyTouchStart);
		window.addEventListener("touchEnd", this.bodyTouchEnd);
	}

	componentDidUpdate(_, prevState) {
		// older versions of react won't leverage getDerivedStateFromProps, TODO: remove when < 16.3 support is dropped
		if (!this.state.hasBeenShown && this.props.isOpen) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ hasBeenShown: true });

			return setTimeout(this.showTip, 0);
		}

		// we need to render once to get refs in place, then we can make the calculations on a followup render
		// this only has to happen the first time the tip is shown, and allows us to not render every tip on the page with initial render.
		if (!prevState.hasBeenShown && this.state.hasBeenShown) {
			this.showTip();
		}
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.listenResizeScroll);
		this.scrollParent.removeEventListener("scroll", this.listenResizeScroll);
		window.removeEventListener("touchstart", this.bodyTouchStart);
		window.removeEventListener("touchEnd", this.bodyTouchEnd);
		clearTimeout(this.debounceTimeout);
		clearTimeout(this.hoverTimeout);
	}

	listenResizeScroll() {
		const originalState = this.state.isOpen;
		this.setState({ isOpen: false });
		clearTimeout(this.debounceTimeout);

		this.debounceTimeout = setTimeout(this.handleResizeScroll, resizeThrottle);

		if (this.state.targetTouch) {
			this.setState({ targetTouch: undefined });
		}
		this.setState({ isOpen: originalState });
	}

	handleResizeScroll() {
		if (this.state.showTip) {
			// if we're showing the tip and the resize was actually a signifigant change, then setState to re-render and calculate position
			const clientWidth = Math.round(document.documentElement.clientWidth / resizeThreshold) * resizeThreshold;
			this.setState({ clientWidth });
		}
	}

	targetTouchStart() {
		this.setState({ targetTouch: true });
	}

	targetTouchEnd() {
		if (this.state.targetTouch) {
			this.toggleTip();
		}
	}

	bodyTouchEnd() {
		if (this.state.targetTouch) {
			this.setState({ targetTouch: undefined });
		}
	}

	bodyTouchStart(e) {
		// if it's a controlled tip we don't want to auto-dismiss, otherwise we just ignore taps inside the tip
		if (
			!(this.target && this.target.contains(e.target)) &&
			!(this.tip && this.tip.contains(e.target)) &&
			!this.props.isOpen
		) {
			this.hideTip();
		}
	}

	toggleTip() {
		this.state.showTip ? this.hideTip() : this.showTip();
	}

	showTip() {
		if (!this.state.hasBeenShown) {
			// this will render once, then fire componentDidUpdate, which will show the tip
			return this.setState({ hasBeenShown: true });
		}

		if (!this.state.showTip) {
			this.setState({ showTip: true }, () => {
				if (typeof this.props.onToggle === "function") {
					this.props.onToggle(this.state.showTip);
				}
			});
		}
	}

	hideTip() {
		this.setState({ hasHover: false });

		if (this.state.showTip) {
			this.setState({ showTip: false }, () => {
				if (typeof this.props.onToggle === "function") {
					this.props.onToggle(this.state.showTip);
				}
			});
		}
	}

	startHover() {
		if (!this.state.ignoreShow) {
			this.setState({ hasHover: true });

			clearTimeout(this.hoverTimeout);
			this.hoverTimeout = setTimeout(this.checkHover, this.props.hoverdelay);
		}
	}

	endHover() {
		this.setState({ hasHover: false });

		clearTimeout(this.hoverTimeout);
		this.hoverTimeout = setTimeout(this.checkHover, this.props.hoverdelay);
	}

	checkHover() {
		this.state.hasHover ? this.showTip() : this.hideTip();
	}

	render() {
		const {
			arrow,
			arrowSize,
			background,
			className,
			children,
			color,
			content,
			direction,
			distance,
			eventOff,
			eventOn,
			eventToggle,
			forceDirection,
			isOpen,
			mouseOutDelay,
			padding,
			styles,
			contentClassName,
			arrowClassName,
			tagName,
			as,
			tipContentHover,
			tipContentClassName,
			useHover,
			arrowContent,
			...properties
		} = this.props;

		const TagName = as || tagName;

		const isControlledByProps = typeof isOpen !== "undefined" && isOpen !== null;
		const showTip = isControlledByProps ? isOpen : this.state.showTip;

		const wrapperStyles = {
			position: "relative",
			...styles,
		};

		const props = {
			style: wrapperStyles,
			ref: (target) => {
				this.target = target;
			},
			className,
			...properties,
		};

		const portalProps = {
			// keep clicks on the tip from closing click controlled tips
			style: { display: this.state.showTip ? undefined: "hidden" },
			onClick: stopProp,
		};

		// event handling
		if (eventOff) {
			props[eventOff] = this.hideTip;
		}

		if (eventOn) {
			props[eventOn] = this.showTip;
		}

		if (eventToggle) {
			props[eventToggle] = this.toggleTip;

			// only use hover if they don't have a toggle event
		} else if (useHover && !isControlledByProps) {
			props.onMouseEnter = tipContentHover ? this.startHover : this.showTip;
			props.onMouseLeave = tipContentHover ? this.endHover : this.hideTip;
			props.onTouchStart = this.targetTouchStart;
			props.onTouchEnd = this.targetTouchEnd;

			if (tipContentHover) {
				portalProps.onMouseEnter = this.startHover;
				portalProps.onMouseLeave = this.endHover;
				portalProps.onTouchStart = stopProp;
			}
		}

		// conditional rendering of tip
		let tipPortal;

		if (this.state.hasBeenShown) {
			const currentPositions = positions(
				direction,
				forceDirection,
				this.tip,
				this.target,
				{ ...this.state, showTip },
				{
					background,
					arrow,
					arrowSize,
					distance,
				},
			);

			const tipStyles = {
				...currentPositions.tip,
				background,
				color,
				padding,
				boxSizing: "border-box",
				zIndex: 9999,
				position: "absolute",
			};

			const arrowStyles = {
				...currentPositions.arrow.positionStyles,
				...(arrowContent ? {} : currentPositions.arrow.borderStyles),
				position: "absolute",
				width: "0px",
				height: "0px",
				zIndex: 9999,
			};

			tipPortal = (
				<Portal>
					<div
						{...portalProps}
						className={typeof tipContentClassName !== "undefined" ? tipContentClassName : className}
					>
						<span
							className={`${Styles.tooltip} ${contentClassName ? contentClassName : "text-xl"}`}
							style={tipStyles}
							ref={(tip) => {
								this.tip = tip;
							}}
						>
							{content}
						</span>
						<span
							className={`Styles.arrow ${arrowClassName ? arrowClassName : ""} react-tooltip-lite-${
								currentPositions.realDirection
							}-arrow`}
							style={arrowStyles}
						>
							{arrowContent}
						</span>
					</div>
				</Portal>
			);
		}

		return (
			<TagName {...props}>
				{children}
				{tipPortal}
			</TagName>
		);
	}
}

export default Tooltip;
